import React, { useMemo } from 'react';
import { Collapse } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { Link } from 'react-router-dom';

import AuthButton from 'components/base/AuthButton';
import CustomDateTimeSelect from 'components/base/CustomDateTimeSelect';
import CustomSwitchButton from 'components/base/CustomSwitchButton';
import {
  ReactHookFormErrorMessage,
  hasError,
} from 'components/base/ErrorFieldMessage';
import {
  campaignLimitDate,
  campaignVisibleClue,
  disableSwitchToAllPeriod,
} from 'components/campaign/campaignCreation/CreateCampaignUtil';

import { TimeFormater, compareDate, formatDate } from 'utils/TimeFormatUtil';

function VisiblePeriodSection() {
  const { watch, setValue, formState } = useFormContext();
  const errors = formState.errors;
  const watchEarningRules = watch('linkedEarningRules');
  const earningRuleType = watchEarningRules?.type;
  const isAlwaysVisiblePeriod = watch('isAlwaysVisiblePeriod');
  const visibleStartDate = watch('visibleStartDate');
  const visibleEndDate = watch('visibleEndDate');
  const relatedBannerCount = watch('relatedBannerCount');
  const relatedBannerFirstOne = watch('relatedBannerFirstOne');
  const relatedBannerStartDate = watch('relatedBannerStartDate');
  const relatedBannerEndDate = watch('relatedBannerEndDate');
  const birthdayStartDate = watchEarningRules?.birthdayTypePeriodStartDate;
  const birthdayEndDate = watchEarningRules?.birthdayTypePeriodEndDate;

  const visibleStartDateReminder = useMemo(() => {
    if (relatedBannerCount > 0) {
      return (
        'The visible period must be equal or longer than the linked campaign’s visible period.\n' +
        `The linked banner start date: ${formatDate(
          relatedBannerStartDate,
          TimeFormater.dayMonthYearWeekTimePeriod,
        )}`
      );
    }
    return '';
  }, [relatedBannerCount, relatedBannerStartDate]);

  const visibleEndDateReminder = useMemo(() => {
    if (relatedBannerCount > 0) {
      return (
        'The visible period must be equal or longer than the linked campaign’s visible period.\n' +
        `The linked banner end date: ${formatDate(
          relatedBannerEndDate,
          TimeFormater.dayMonthYearWeekTimePeriod,
        )}`
      );
    }
    return '';
  }, [relatedBannerCount, relatedBannerEndDate]);

  const EditBannerButton = () => {
    if (relatedBannerCount > 1) {
      return (
        <Link to={{ pathname: '/banners' }} target="_blank">
          <AuthButton
            title="Banner list"
            customClass={'general-section-add-new btn-add-button-common'}
            action={() => {}}
          />
        </Link>
      );
    }
    if (relatedBannerCount === 1 && relatedBannerFirstOne?.pk) {
      return (
        <Link
          to={{ pathname: `/banners/${relatedBannerFirstOne?.pk}/edit/` }}
          target="_blank"
        >
          <AuthButton
            title="Edit banner"
            customClass={'general-section-add-new btn-add-button-common'}
          />
        </Link>
      );
    }
    return null;
  };

  return (
    <>
      <label className="create-section-title">Visible Period</label>
      <label className="section-short-description">
        {campaignVisibleClue(earningRuleType)}
      </label>
      <label className="create-section-label create-section-label-bottom-space">
        It is an all-time visible campaign
      </label>
      <CustomSwitchButton
        disabled={disableSwitchToAllPeriod(
          earningRuleType,
          birthdayStartDate,
          birthdayEndDate,
        )}
        defaultChecked={isAlwaysVisiblePeriod}
        onChange={(value) => {
          setValue('isAlwaysVisiblePeriod', value, { shouldDirty: true });
        }}
      />
      <Collapse in={!isAlwaysVisiblePeriod}>
        <div>
          <div>
            <label className="create-section-label create-section-label-bottom-space">
              Start date
            </label>
            <CustomDateTimeSelect
              disabled={isAlwaysVisiblePeriod}
              defaultTime={compareDate(
                visibleStartDate,
                birthdayStartDate,
                false,
              )}
              maxDate={campaignLimitDate(earningRuleType, birthdayStartDate)}
              minDate={null}
              error={hasError(errors, 'visibleStartDate')}
              onTimeChange={(date) => {
                setValue('visibleStartDate', date, { shouldDirty: true });
              }}
            />
            {hasError(errors, 'visibleStartDate') ? (
              <ReactHookFormErrorMessage
                errors={errors}
                id={'visibleStartDate'}
              />
            ) : (
              <label className="tips-message">
                {visibleStartDateReminder.split('\n').map((item, index) => (
                  <>
                    {index === 0 ? null : <br />}
                    <>{item}</>
                  </>
                ))}
              </label>
            )}
          </div>
          <EditBannerButton />
          <div>
            <label className="create-section-label create-section-label-bottom-space">
              End date
            </label>
            <CustomDateTimeSelect
              disabled={isAlwaysVisiblePeriod}
              defaultTime={compareDate(visibleEndDate, birthdayEndDate, true)}
              maxDate={null}
              minDate={campaignLimitDate(earningRuleType, birthdayEndDate)}
              error={hasError(errors, 'visibleEndDate')}
              onTimeChange={(date) => {
                setValue('visibleEndDate', date, { shouldDirty: true });
              }}
            />
            {hasError(errors, 'visibleEndDate') ? (
              <ReactHookFormErrorMessage
                errors={errors}
                id={'visibleEndDate'}
              />
            ) : (
              <label className="tips-message">
                {visibleEndDateReminder.split('\n').map((item, index) => (
                  <>
                    {index === 0 ? null : <br />}
                    <>{item}</>
                  </>
                ))}
              </label>
            )}
          </div>
          <EditBannerButton />
        </div>
      </Collapse>
    </>
  );
}

export default VisiblePeriodSection;
